import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useTheme } from '../../../contexts/ThemeContext';

const CombinedFilter = ({
  filterData,
  activeFilters,
  onCategoryToggle,
  onSubcategoryChange,
  onReset,
  priceRange,
  onPriceChange,
  maxPrice,
}) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  // This assumes activeFilters is now an object like { categories: [], subcategories: [] }
  const isFilterActive =
    (activeFilters?.categories?.length > 0) ||
    (activeFilters?.subcategories?.length > 0) ||
    priceRange?.min > 0 ||
    priceRange?.max < maxPrice;

  const activeFiltersCount = 
    (activeFilters?.categories?.length || 0) + 
    (activeFilters?.subcategories?.length || 0) +
    ((priceRange?.min > 0 || priceRange?.max < maxPrice) ? 1 : 0);

  return (
    <div className="relative">
      {/* Filter Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
          isDark 
            ? 'bg-surface border-street hover:border-accent shadow-lg' 
            : 'bg-white border-blue-200 hover:border-blue-400 shadow-xl shadow-blue-500/10'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center space-x-3">
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <Icon 
              name="SlidersHorizontal" 
              size={24} 
              className={isDark ? 'text-accent' : 'text-blue-600'} 
            />
          </motion.div>
          <span className={`font-bold text-lg ${
            isDark ? 'text-foreground' : 'text-slate-900'
          }`}>
            Filtres
          </span>
          {activeFiltersCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                isDark 
                  ? 'bg-accent text-background' 
                  : 'bg-blue-600 text-white'
              }`}
            >
              {activeFiltersCount}
            </motion.span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <Icon 
            name="ChevronDown" 
            size={20} 
            className={isDark ? 'text-muted-foreground' : 'text-slate-600'} 
          />
        </motion.div>
      </motion.button>

      {/* Filter Panel - Collapsible */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className={`mt-4 p-6 rounded-xl border transition-all ${
              isDark 
                ? 'bg-surface shadow-sm border-street' 
                : 'bg-white shadow-xl shadow-blue-500/10 border-blue-200/50'
            }`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`font-bold text-xl ${
                  isDark ? 'text-foreground' : 'text-slate-900'
                }`}>Filtres actifs</h3>
                {isFilterActive && ( 
                  <motion.button
                    onClick={onReset}
                    className={`text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center space-x-2 ${
                      isDark 
                        ? 'text-muted-foreground hover:text-foreground hover:bg-surface/50 border border-street' 
                        : 'text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon name="RotateCcw" size={16} />
                    <span>Réinitialiser</span>
                  </motion.button>
                )}
              </div>

              {/* Categories & Subcategories */}
              <div className="space-y-4">
                {filterData?.map((category) => (
                  <motion.div 
                    key={category.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        className={`w-5 h-5 rounded-md focus:ring-2 cursor-pointer transition-all ${
                          isDark 
                            ? 'text-accent bg-surface border-street focus:ring-accent' 
                            : 'text-blue-600 bg-white border-blue-300 focus:ring-blue-500 checked:bg-blue-600 checked:border-blue-600'
                        }`}
                        checked={activeFilters.categories?.includes(category.id) || false}
                        onChange={(e) => onCategoryToggle(category.id, e.target.checked)}
                      />
                      <span className={`font-bold transition-all ${
                        isDark 
                          ? 'text-foreground group-hover:text-accent' 
                          : 'text-slate-900 group-hover:text-blue-600'
                      }`}>{category.name}</span>
                    </label>

                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className={`pl-6 mt-2 space-y-2 border-l-2 ${
                        isDark ? 'border-street' : 'border-blue-200'
                      }`}>
                        {category.subcategories.map((subcategory) => (
                          <label key={subcategory.id} className="flex items-center space-x-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              className={`w-4 h-4 rounded cursor-pointer transition-all ${
                                isDark 
                                  ? 'text-accent bg-surface border-street focus:ring-accent focus:ring-2' 
                                  : 'text-blue-600 bg-white border-blue-300 focus:ring-blue-500 focus:ring-2 checked:bg-blue-600 checked:border-blue-600'
                              }`}
                              checked={activeFilters.subcategories?.includes(subcategory.id) || false}
                              onChange={(e) => onSubcategoryChange(subcategory.id, e.target.checked, category.id)}
                            />
                            <span className={`text-sm transition-colors ${
                              isDark 
                                ? 'text-muted-foreground group-hover:text-foreground' 
                                : 'text-slate-600 group-hover:text-slate-900'
                            }`}>{subcategory.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Price Range Filter */}
              <div className={`mt-6 pt-6 border-t ${
                isDark ? 'border-street' : 'border-blue-200'
              }`}>
                <h4 className={`font-bold text-lg mb-4 flex items-center space-x-2 ${
                  isDark ? 'text-foreground' : 'text-slate-900'
                }`}>
                  <Icon name="DollarSign" size={20} className={isDark ? 'text-accent' : 'text-blue-600'} />
                  <span>Fourchette de prix</span>
                </h4>
                
                <div className="mb-4">
                    <label className={`flex justify-between text-sm font-medium mb-2 ${
                      isDark ? 'text-muted-foreground' : 'text-slate-600'
                    }`}>
                        <span>Prix minimum</span>
                        <span className={`font-bold ${isDark ? 'text-accent' : 'text-blue-600'}`}>{priceRange.min} TND</span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max={maxPrice}
                        value={priceRange.min}
                        onChange={(e) => {
                            const newMin = Math.min(Number(e.target.value), priceRange.max - 1);
                            onPriceChange({ ...priceRange, min: newMin });
                        }}
                        className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                          isDark ? 'bg-gray-700' : 'bg-blue-200'
                        }`}
                        style={{
                          accentColor: isDark ? '#00ffc3' : '#3B82F6'
                        }}
                    />
                </div>

                <div>
                    <label className={`flex justify-between text-sm font-medium mb-2 ${
                      isDark ? 'text-muted-foreground' : 'text-slate-600'
                    }`}>
                        <span>Prix maximum</span>
                        <span className={`font-bold ${isDark ? 'text-accent' : 'text-blue-600'}`}>{priceRange.max} TND</span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max={maxPrice}
                        value={priceRange.max}
                        onChange={(e) => {
                            const newMax = Math.max(Number(e.target.value), priceRange.min + 1);
                            onPriceChange({ ...priceRange, max: newMax });
                        }}
                        className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                          isDark ? 'bg-gray-700' : 'bg-blue-200'
                        }`}
                        style={{
                          accentColor: isDark ? '#00ffc3' : '#3B82F6'
                        }}
                    />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CombinedFilter;